import { FC, useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import {
  Button,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { ExternalLink, Flag, X } from "lucide-react";
import ConsolidationFeedbackForm from "@/components/dialogs/ConsolidationFeedbackForm";
import {
  getConsolidationCannotHighlightDiffMessage,
  getConsolidationFullDeletionMessage,
  getConsolidationFullInsertionMessage,
  getConsolidationInstructionPanelSubtitle,
  getConsolidationNoChangesMessage,
} from "@/lib/consolidationContainerLabels";
import { resolveTimelineLabel } from "@/lib/consolidationViewerUtils";
import type {
  ContainerChangeDetail,
  ConsolidationView,
} from "@/models/consolidation";
import type { TimelineItem } from "@/models/legislation/Timeline";

type ConsolidationChangeInstructionPanelProps = {
  containerType?: string | null;
  data: ContainerChangeDetail[] | undefined;
  isLoading: boolean;
  isError: boolean;
  cannotHighlightDiff?: boolean;
  /** The container was inserted whole (no prior version to diff against). */
  isFullInsertion?: boolean;
  /** The container was deleted whole (no post version to diff against). */
  isFullDeletion?: boolean;
  onClose: () => void;
  /** Timeline for resolving change-law accession IDs to short names (e.g. Lov nr.). */
  timelineItems?: TimelineItem[];
  onNavigateToChangeLaw?: (changeLawExternalId: string) => void;
  feedback?: {
    lawExternalId: string;
    asOf: string;
    view: ConsolidationView;
    containerId: string;
  } | null;
};

const formatChangeStatus = (status: string): string => {
  if (status === "APPROVED") return "Godkendt";
  if (status === "NEEDS_REVIEW") return "Kræver gennemgang";
  return status;
};

const sanitizeInstructionHtml = (html: string | null | undefined): string | null => {
  if (!html?.trim()) return null;
  return DOMPurify.sanitize(html, {
    ALLOW_UNKNOWN_PROTOCOLS: false,
    WHOLE_DOCUMENT: false,
  });
};

const ConsolidationChangeInstructionPanel: FC<
  ConsolidationChangeInstructionPanelProps
> = ({
  containerType,
  data,
  isLoading,
  isError,
  cannotHighlightDiff = false,
  isFullInsertion = false,
  isFullDeletion = false,
  onClose,
  timelineItems,
  onNavigateToChangeLaw,
  feedback = null,
}) => {
  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const changes = useMemo(() => data ?? [], [data]);
  const hasChanges = changes.length > 0;
  const hasMultipleChanges = changes.length > 1;

  const showFeedbackFooter = feedback && hasChanges && !isLoading;

  useEffect(() => {
    setFeedbackExpanded(false);
  }, [feedback?.containerId]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0 space-y-1">
          <h2 className="text-base font-semibold leading-tight">
            Ændringsinstruktion
          </h2>
          <p className="text-sm text-muted-foreground">
            {getConsolidationInstructionPanelSubtitle(
              containerType,
              hasMultipleChanges
            )}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          onClick={onClose}
          aria-label="Luk ændringsinstruktion"
        >
          <X className="size-4" />
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Spinner size="medium" />
          </div>
        )}

        {isError && (
          <p className="text-sm text-muted-foreground">
            Ændringsinstruktionen kunne ikke hentes.
          </p>
        )}

        {isFullInsertion && !isFullDeletion && !isLoading && hasChanges && (
          <p className="text-xs text-muted-foreground">
            {getConsolidationFullInsertionMessage(containerType)}
          </p>
        )}

        {isFullDeletion && !isLoading && hasChanges && (
          <p className="text-xs text-muted-foreground">
            {getConsolidationFullDeletionMessage(containerType)}
          </p>
        )}

        {cannotHighlightDiff &&
          !isFullInsertion &&
          !isFullDeletion &&
          !isLoading &&
          hasChanges && (
            <p className="text-xs text-muted-foreground">
              {getConsolidationCannotHighlightDiffMessage(containerType)}
            </p>
          )}

        {!isLoading && !isError && !hasChanges && (
          <p className="text-sm text-muted-foreground">
            {getConsolidationNoChangesMessage(containerType)}
          </p>
        )}

        {!isLoading && hasChanges && (
          <ol className="space-y-4">
            {changes.map((change, index) => {
              const instructionHtml = sanitizeInstructionHtml(
                change.changeInstructionHtml
              );
              return (
                <li
                  key={`${change.changeId}-${change.applyOrder}`}
                  className="space-y-3"
                >
                  <div className="flex items-start gap-3">
                    {hasMultipleChanges && (
                      <span
                        aria-hidden="true"
                        className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary"
                      >
                        {index + 1}
                      </span>
                    )}
                    <div className="min-w-0 flex-1 space-y-1 text-sm">
                      <p className="text-muted-foreground">
                        Ændringsdokument:{" "}
                        <span className="font-medium text-foreground">
                          {resolveTimelineLabel(
                            change.changeLawExternalId,
                            timelineItems
                          )}
                        </span>
                      </p>
                      <p className="text-muted-foreground">
                        Status:{" "}
                        <span className="font-medium text-foreground">
                          {formatChangeStatus(change.status)}
                        </span>
                      </p>
                    </div>
                  </div>

                  {instructionHtml ? (
                    <div className="rounded-md border border-border bg-muted/30 p-4 text-sm document-content [&_.AendringMedNummer]:font-medium">
                      {parse(instructionHtml)}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Ingen instruktionstekst tilgængelig for denne ændring.
                    </p>
                  )}

                  {onNavigateToChangeLaw && change.changeLawExternalId && (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-fit"
                      onClick={() =>
                        onNavigateToChangeLaw(change.changeLawExternalId)
                      }
                    >
                      <ExternalLink className="mr-2 h-4 w-4" />
                      Gå til ændringsdokument på tidslinjen
                    </Button>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {showFeedbackFooter && (
        <div className="shrink-0 border-t border-border bg-muted/20 px-4 py-3">
          {feedbackExpanded ? (
            <ConsolidationFeedbackForm
              lawExternalId={feedback.lawExternalId}
              asOf={feedback.asOf}
              view={feedback.view}
              containerId={feedback.containerId}
              containerType={containerType}
              compact
              onCancel={() => setFeedbackExpanded(false)}
              onSuccess={() => setFeedbackExpanded(false)}
            />
          ) : (
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start gap-2"
              onClick={() => setFeedbackExpanded(true)}
            >
              <Flag className="size-4 shrink-0" />
              Rapportér en fejl
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ConsolidationChangeInstructionPanel;
